import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { head } from "lodash";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MessageVariablesPicker from "../MessageVariablesPicker";
import ConfirmationModal from "../ConfirmationModal";
import LogoLoader from "../LogoLoader";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";

const path = require("path");

const useStyles = makeStyles((theme) => ({
  slash: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginRight: -4,
  },
  shortcodeInput: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
  attachmentRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1, 1, 1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  attachmentName: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  attachButton: {
    justifyContent: "flex-start",
    height: 44,
    borderRadius: 10,
    borderStyle: "dashed",
    textTransform: "none",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  },
  deleteAction: {
    color: theme.palette.text.secondary,
    "&:hover": { color: theme.palette.error.main },
  },
}));

// o "/" é só o gatilho no chat; o atalho é salvo sem ele
const normalizeShortcode = (value) => String(value || "").trim().replace(/^\/+/, "");

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string()
    .transform(normalizeShortcode)
    .required("Informe o atalho"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const messageInputRef = useRef();

  const initialState = {
    shortcode: "",
    message: "",
    geral: false,
    status: true,
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);

  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    (async () => {
      if (!quickemessageId) return;
      setLoadingData(true);
      try {
        const { data } = await api.get(`/quick-messages/${quickemessageId}`);

        setQuickemessage((prevState) => {
          return { ...prevState, ...data };
        });
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingData(false);
      }
    })();
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveQuickeMessage = async (values) => {
    const quickemessageData = {
      ...values,
      shortcode: normalizeShortcode(values.shortcode),
      isMedia: true,
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
        ? path.basename(values.mediaPath).replace(/ /g, "_")
        : null,
    };

    try {
      if (quickemessageId) {
        await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${quickemessageId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload == "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload == "function") {
        reload();
      }
    }
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const hasAttachment = Boolean(attachment || quickemessage.mediaPath);

  return (
    <>
      <ConfirmationModal
        title="Remover anexo?"
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        O arquivo deixará de ser enviado junto com esta resposta.
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: dialogClasses.paper }}
        aria-labelledby="quick-message-title"
      >
        {loadingData && <LogoLoader overlay />}
        <input
          type="file"
          ref={attachmentFile}
          onChange={(e) => handleAttachmentFile(e)}
          style={{ display: "none" }}
        />
        <Formik
          initialValues={quickemessage}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={async (values) => {
            // Aguarda a requisição terminar: enquanto isso o Formik mantém
            // isSubmitting=true e o botão desabilitado, evitando envio duplicado.
            await handleSaveQuickeMessage(values);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form style={{ display: "contents" }}>
              <FormDialogHeader
                id="quick-message-title"
                title={quickemessageId ? "Editar resposta rápida" : "Nova resposta rápida"}
                subtitle="No chat, digite / seguido do atalho para inserir esta resposta."
                onClose={handleClose}
                disabled={isSubmitting}
              />

              <DialogContent dividers className={dialogClasses.content}>
                <FormSection title="Atalho">
                  <Field
                    as={TextField}
                    autoFocus
                    name="shortcode"
                    placeholder="precos"
                    // a "/" já aparece fixa no campo; não deixa digitar outra
                    onChange={(e) =>
                      setFieldValue("shortcode", e.target.value.replace(/^\/+/, ""))
                    }
                    error={touched.shortcode && Boolean(errors.shortcode)}
                    helperText={
                      (touched.shortcode && errors.shortcode) ||
                      `No chat: /${normalizeShortcode(values.shortcode) || "atalho"}`
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    inputProps={{ "aria-label": "Atalho", className: classes.shortcodeInput }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <span className={classes.slash}>/</span>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormSection>

                <FormSection title="Mensagem">
                  <Field
                    as={TextField}
                    name="message"
                    placeholder="Ex.: Olá {{firstName}}! Segue nossa tabela de preços…"
                    inputRef={messageInputRef}
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={6}
                    maxRows={14}
                    fullWidth
                    inputProps={{ "aria-label": "Mensagem" }}
                  />
                  <span className={dialogClasses.muted} style={{ fontSize: 12 }}>
                    Clique numa variável para inserir no ponto do cursor:
                  </span>
                  <MessageVariablesPicker
                    variant="inline"
                    disabled={isSubmitting}
                    onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                  />
                </FormSection>

                <FormSection title="Anexo">
                  {hasAttachment ? (
                    <div className={classes.attachmentRow}>
                      <AttachFileIcon fontSize="small" className={dialogClasses.muted} />
                      <span className={classes.attachmentName}>
                        {attachment ? attachment.name : quickemessage.mediaName}
                      </span>
                      <Tooltip title="Remover anexo">
                        <IconButton
                          size="small"
                          className={classes.deleteAction}
                          onClick={() => setConfirmationOpen(true)}
                          aria-label="Remover anexo"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      className={classes.attachButton}
                      startIcon={<AttachFileIcon />}
                      onClick={() => attachmentFile.current.click()}
                      disabled={isSubmitting}
                    >
                      Anexar arquivo (imagem, PDF, áudio…) — opcional
                    </Button>
                  )}
                </FormSection>
              </DialogContent>

              <DialogActions className={dialogClasses.actions}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  disableElevation
                  className={`${dialogClasses.actionButton} ${dialogClasses.progressWrapper}`}
                >
                  {quickemessageId ? "Salvar alterações" : "Salvar resposta"}
                  {isSubmitting && (
                    <CircularProgress size={24} className={dialogClasses.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default QuickMessageDialog;
